import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Copy, Trash2, Eye, FileDown, ChevronUp, ChevronDown,
  Pencil, FileSpreadsheet, FileText, Layers,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZES = [
  { value: "A4", label: "A4" },
  { value: "A3", label: "A3" },
  { value: "LETTER", label: "Carta" },
  { value: "LEGAL", label: "Ofício" },
];

const emptyTemplate = {
  name: "", description: "", entity: "employees",
  columns: [], filters: {}, page_size: "A4",
  orientation: "landscape", show_summary: true,
};

export default function ReportDesignerPage() {
  const { hasRole } = useAuth();
  const canCreate = hasRole("master", "rh_admin", "rh_operational", "directors");
  const canDelete = hasRole("master", "rh_admin");
  const [entities, setEntities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filterEntity, setFilterEntity] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(emptyTemplate);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const load = async () => {
    try {
      const [e, t] = await Promise.all([
        api.get("/report-designer/entities"),
        api.get("/report-designer/templates"),
      ]);
      setEntities(e.data.entities || []);
      setTemplates(t.data.items || []);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filteredTpls = useMemo(() => {
    if (filterEntity === "all") return templates;
    return templates.filter((t) => t.entity === filterEntity);
  }, [templates, filterEntity]);

  const entityByKey = useMemo(() =>
    Object.fromEntries(entities.map((e) => [e.key, e])), [entities]);

  const openCreate = () => {
    setEditing(null);
    const firstEntity = entities[0]?.key || "employees";
    const fields = entityByKey[firstEntity]?.fields || [];
    setForm({
      ...emptyTemplate, entity: firstEntity,
      columns: fields.slice(0, 6).map((f) => ({ key: f.key, label: f.label, visible: true })),
    });
    setEditorOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      ...emptyTemplate, ...t,
      columns: t.columns || [],
    });
    setEditorOpen(true);
  };

  const duplicate = async (t) => {
    try {
      const { data } = await api.post(`/report-designer/templates/${t.id}/duplicate`);
      toast.success("Cópia criada");
      await load();
      openEdit(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (t) => {
    if (t.is_system) {
      toast.error("Modelo padrão não pode ser excluído. Use Duplicar.");
      return;
    }
    if (!window.confirm(`Excluir modelo "${t.name}"?`)) return;
    try {
      await api.delete(`/report-designer/templates/${t.id}`);
      toast.success("Modelo excluído");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome do modelo."); return; }
    if (!form.columns.length) { toast.error("Selecione ao menos 1 campo."); return; }
    try {
      if (editing && !editing.is_system) {
        await api.put(`/report-designer/templates/${editing.id}`, form);
        toast.success("Modelo atualizado");
      } else if (editing && editing.is_system) {
        toast.error("Modelo padrão não pode ser editado. Use Duplicar.");
        return;
      } else {
        await api.post("/report-designer/templates", form);
        toast.success("Modelo criado");
      }
      setEditorOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const preview = async (t) => {
    try {
      const { data } = await api.post(
        `/report-designer/templates/${t.id}/run`, { limit: 100 });
      setPreviewData({ template: t, ...data });
      setPreviewOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportReport = async (t, format) => {
    try {
      const token = localStorage.getItem("rh_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/report-designer/templates/${t.id}/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ format, limit: 5000 }),
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ext = format === "excel" ? "xlsx" : format;
      a.download = `${t.name.replace(/\s+/g, "-")}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${format.toUpperCase()} gerado`);
    } catch { toast.error("Falha ao exportar"); }
  };

  // Column manipulation in editor
  const availableFields = entityByKey[form.entity]?.fields || [];
  const addColumn = (key) => {
    if (form.columns.find((c) => c.key === key)) return;
    const f = availableFields.find((ff) => ff.key === key);
    if (!f) return;
    setForm({ ...form, columns: [...form.columns, { key: f.key, label: f.label, visible: true }] });
  };
  const removeColumn = (key) => setForm({ ...form, columns: form.columns.filter((c) => c.key !== key) });
  const moveCol = (idx, dir) => {
    const arr = [...form.columns];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setForm({ ...form, columns: arr });
  };
  const updateColLabel = (key, label) => setForm({
    ...form, columns: form.columns.map((c) => c.key === key ? { ...c, label } : c),
  });

  // Entity change resets columns
  const changeEntity = (newEntity) => {
    const fields = entityByKey[newEntity]?.fields || [];
    setForm({
      ...form, entity: newEntity,
      columns: fields.slice(0, 6).map((f) => ({ key: f.key, label: f.label, visible: true })),
    });
  };

  return (
    <div className="space-y-6" data-testid="report-designer-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Plataforma · Relatórios</div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <Layers className="h-7 w-7 text-[#234935]" /> Designer de Relatórios
          </h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
            Crie, edite e personalize modelos de relatório para qualquer entidade do sistema. Exporte em PDF, Excel ou CSV. Modelos padrão protegidos podem ser duplicados e personalizados.
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="new-template-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo modelo personalizado
          </Button>
        )}
      </div>

      <div className="rounded-lg border bg-white p-4 flex items-center gap-3 flex-wrap">
        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Entidade</Label>
        <Select value={filterEntity} onValueChange={setFilterEntity}>
          <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas entidades</SelectItem>
            {entities.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-[12px] text-muted-foreground">
          {filteredTpls.length} modelo(s)
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filteredTpls.map((t) => (
          <div key={t.id} className="rounded-lg border bg-white p-4 space-y-3" data-testid={`tpl-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-semibold text-[15px] leading-tight">{t.name}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{entityByKey[t.entity]?.label}</div>
              </div>
              {t.is_system && <Badge variant="outline" className="bg-[#F1ECE0] text-[#5A4519] ring-1 ring-[#C5A059]/30 text-[10px]">PADRÃO</Badge>}
            </div>
            {t.description && <div className="text-[12px] text-muted-foreground line-clamp-2">{t.description}</div>}
            <div className="text-[10.5px] text-muted-foreground">
              {(t.columns || []).length} campos · {(t.page_size || "A4")} {t.orientation === "portrait" ? "retrato" : "paisagem"}
            </div>
            <div className="flex items-center gap-1 flex-wrap pt-2 border-t">
              <Button size="sm" variant="ghost" onClick={() => preview(t)} data-testid={`tpl-preview-${t.id}`}>
                <Eye className="h-3.5 w-3.5 mr-1" /> Prévia
              </Button>
              <Button size="sm" variant="ghost" onClick={() => exportReport(t, "pdf")} data-testid={`tpl-pdf-${t.id}`}>
                <FileText className="h-3.5 w-3.5 mr-1" /> PDF
              </Button>
              <Button size="sm" variant="ghost" onClick={() => exportReport(t, "excel")}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
              </Button>
              <Button size="sm" variant="ghost" onClick={() => exportReport(t, "csv")}>
                <FileDown className="h-3.5 w-3.5 mr-1" /> CSV
              </Button>
              <div className="ml-auto flex items-center gap-1">
                {canCreate && (
                  <Button size="icon" variant="ghost" onClick={() => duplicate(t)} title="Duplicar">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canCreate && !t.is_system && (
                  <Button size="icon" variant="ghost" onClick={() => openEdit(t)} title="Editar">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canDelete && !t.is_system && (
                  <Button size="icon" variant="ghost" onClick={() => del(t)} title="Excluir">
                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Editar: ${editing.name}` : "Novo modelo personalizado"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Nome do modelo</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Entidade</Label>
              <Select value={form.entity} onValueChange={changeEntity}>
                <SelectTrigger data-testid="tpl-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {entities.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
              <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tamanho página</Label>
              <Select value={form.page_size} onValueChange={(v) => setForm({ ...form, page_size: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Orientação</Label>
              <Select value={form.orientation} onValueChange={(v) => setForm({ ...form, orientation: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Retrato</SelectItem>
                  <SelectItem value="landscape">Paisagem</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end p-3 bg-slate-50 rounded-md gap-3">
              <Switch checked={!!form.show_summary} onCheckedChange={(v) => setForm({ ...form, show_summary: v })} />
              <div>
                <div className="text-[12.5px] font-medium">Exibir resumo</div>
                <div className="text-[11px] text-muted-foreground">Total de registros no rodapé</div>
              </div>
            </div>
          </div>

          {/* Columns picker */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="border rounded-lg bg-slate-50 p-3 max-h-[360px] overflow-y-auto">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                Campos disponíveis ({availableFields.length})
              </div>
              <div className="space-y-1">
                {availableFields.map((f) => {
                  const used = !!form.columns.find((c) => c.key === f.key);
                  return (
                    <button key={f.key}
                      disabled={used}
                      onClick={() => addColumn(f.key)}
                      className={`w-full text-left px-2 py-1.5 rounded text-[12.5px] flex items-center justify-between ${used ? "opacity-50 cursor-not-allowed bg-white" : "bg-white hover:bg-emerald-50 cursor-pointer"}`}
                      data-testid={`add-field-${f.key}`}>
                      <span>{f.label}</span>
                      <span className="text-[10px] text-muted-foreground">{f.type}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="border rounded-lg p-3 max-h-[360px] overflow-y-auto">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                Colunas do relatório ({form.columns.length})
              </div>
              <div className="space-y-1">
                {form.columns.map((c, idx) => (
                  <div key={c.key} className="flex items-center gap-2 bg-white border rounded px-2 py-1.5" data-testid={`col-${c.key}`}>
                    <Button size="icon" variant="ghost" disabled={idx === 0}
                            onClick={() => moveCol(idx, -1)} className="h-6 w-6">
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button size="icon" variant="ghost" disabled={idx === form.columns.length - 1}
                            onClick={() => moveCol(idx, +1)} className="h-6 w-6">
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Input value={c.label}
                           onChange={(e) => updateColLabel(c.key, e.target.value)}
                           className="h-7 text-[12.5px] flex-1" />
                    <Button size="icon" variant="ghost" onClick={() => removeColumn(c.key)}
                            className="h-6 w-6">
                      <Trash2 className="h-3 w-3 text-rose-600" />
                    </Button>
                  </div>
                ))}
                {!form.columns.length && (
                  <div className="text-center py-6 text-[12px] text-muted-foreground">
                    Adicione campos do painel esquerdo
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="tpl-save">
              {editing && !editing.is_system ? "Salvar alterações" : "Criar modelo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              Prévia: {previewData?.template?.name}
            </DialogTitle>
          </DialogHeader>
          {previewData && (
            <>
              <div className="text-[12px] text-muted-foreground mb-2">
                {previewData.total} registros · {previewData.columns?.length} colunas
              </div>
              <div className="rounded border max-h-[500px] overflow-auto">
                <Table>
                  <TableHeader><TableRow>
                    {(previewData.columns || []).map((c) => (
                      <TableHead key={c.key}>{c.label}</TableHead>
                    ))}
                  </TableRow></TableHeader>
                  <TableBody>
                    {(previewData.rows || []).slice(0, 100).map((r, i) => (
                      <TableRow key={i}>
                        {previewData.columns.map((c) => (
                          <TableCell key={c.key} className="text-[12px]">{r[c.key]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
